/**
 * US-257 — Texas Operations Console (a single page where an operator runs and
 * observes ops tasks). Aggregates EXISTING backend endpoints ONLY — no new
 * BE routes. Every task cites the source route it drives; every status probe
 * and run action resolves through the existing services (texasApi, usdBilling,
 * floodApi, cityOverridesApi). No DataDog/CloudWatch vendor dependencies.
 *
 * Registered at /texas/ops-console (group 'other', URL-reachable) with
 * routeRegistry <-> App.tsx parity; market-gated to texas.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { getAppMarket } from '../../services/MarketService';
import { landlordApi } from '../../services/api';
import texasApi from '../../services/texasApi';
import { usdBillingApi } from '../../services/usdBilling';
import { floodApi } from '../../services/floodApi';
import { cityOverridesApi } from '../../services/cityOverridesApi';

export interface OpsTaskResult {
  ok: boolean;
  summary: string;
}

export interface OpsTask {
  key: string;
  group: 'billing' | 'compliance' | 'stream' | 'probes';
  label: string;
  description: string;
  source: string;
  status: () => Promise<OpsTaskResult>;
  run?: () => Promise<OpsTaskResult>;
}

export const OPS_GROUPS: { key: OpsTask['group']; label: string }[] = [
  { key: 'billing', label: 'Billing & Metering' },
  { key: 'compliance', label: 'Compliance Checks' },
  { key: 'stream', label: 'Event Stream & Deadlines' },
  { key: 'probes', label: 'Engine Status Probes' },
];

// ── response formatting helpers ───────────────────────────────────────────────
function unwrapBody(res: any): any {
  const body = res?.data && typeof res.data === 'object' ? (res.data as any) : res;
  if (body && typeof body === 'object' && !Array.isArray(body) && 'data' in body) return body.data;
  return body ?? {};
}

function fmt(d: any, keys?: string[]): string {
  if (d === null || d === undefined) return 'no data';
  if (Array.isArray(d)) return `${d.length} item(s)`;
  if (typeof d !== 'object') return String(d);
  const wanted = keys?.length
    ? keys
    : Object.keys(d)
        .filter((k) => {
          const v = d[k];
          return typeof v !== 'object' || v === null;
        })
        .slice(0, 6);
  const parts = wanted
    .map((k) => {
      const v = d[k];
      const val = typeof v === 'number' && Number.isFinite(v) ? Math.round(v * 100) / 100 : v;
      return `${k}=${val ?? 'n/a'}`;
    })
    .join(' | ');
  return parts || 'ok';
}

async function read(fetch: () => Promise<any>): Promise<string> {
  return fmt(unwrapBody(await fetch()));
}

const metaStatus = (fetch: () => Promise<any>): (() => Promise<OpsTaskResult>) => async () => {
  try {
    return { ok: true, summary: await read(fetch) };
  } catch (err: any) {
    return { ok: false, summary: err?.message || 'failed' };
  }
};

const runPost = (fetch: () => Promise<any>): (() => Promise<OpsTaskResult>) => async () => {
  try {
    const payload = unwrapBody(await fetch());
    return { ok: true, summary: fmt(payload, ['activeUnits', 'unitChanges', 'updatedAt', 'ok', 'count', 'swept', 'dueSoon', 'remaining', 'notified']) || 'queued' };
  } catch (err: any) {
    return { ok: false, summary: err?.message || 'run failed' };
  }
};

// Some endpoints answer with a non-2xx status that carries a verdict rather than
// a failure. POST /ccpa/check returns 422 when the tenant has violations, which
// is the check working; reporting it as a broken task hides a real finding.
const VERDICT_STATUSES = new Set([400, 422]);
const runPostVerdict = (fetch: () => Promise<any>): (() => Promise<OpsTaskResult>) => async () => {
  try {
    const payload = unwrapBody(await fetch());
    return { ok: true, summary: fmt(payload, ['compliant', 'ok', 'count']) || 'answered' };
  } catch (err: any) {
    const status = err?.response?.status;
    if (VERDICT_STATUSES.has(status)) {
      const body = err?.response?.data;
      const verdict =
        body?.data?.compliant === false ? 'violations found'
        : body?.message || 'answered';
      return { ok: true, summary: `HTTP ${status} — ${verdict}` };
    }
    return { ok: false, summary: err?.message || 'run failed' };
  }
};

// ── the ops task catalogue (every backend path pre-existing) ──────────────────
export const OPS_TASKS: OpsTask[] = [
  // Billing & Metering (usd-billing.js)
  {
    key: 'meter',
    group: 'billing',
    label: 'Meter position',
    description: 'Live metered units, unit-log size and billing cycle for the account.',
    source: 'usd-billing.js GET /meter :104',
    status: metaStatus(() => usdBillingApi.getMeter()),
  },
  {
    key: 'meter-sync',
    group: 'billing',
    label: 'Reconcile metered units',
    description: 'Re-open the meter against the current property list (countActiveUnits).',
    source: 'usd-billing.js POST /meter/sync :134',
    status: metaStatus(() => usdBillingApi.getMeter()),
    run: runPost(() => usdBillingApi.syncMeter()),
  },
  // Compliance Checks (ccpa.js, ccpa-dsr.js)
  {
    key: 'ccpa-scan',
    group: 'compliance',
    label: 'CCPA compliance check',
    description: 'Run the CCPA compliance checker against the current setup.',
    source: 'routes/ccpa.js POST /api/texas/ccpa/check (texasApi.ccpa.runCheck)',
    status: metaStatus(() => texasApi.ccpa.getMeta()),
    run: runPostVerdict(() => texasApi.ccpa.runCheck({})),
  },
  {
    key: 'ccpa-dsr-sweep',
    group: 'compliance',
    label: 'CCPA DSR deadline sweep',
    description: 'Sweep open data-subject requests for due deadlines.',
    source: 'routes/ccpa-dsr.js GET /api/texas/ccpa/dsr/deadlines/sweep (texasApi.ccpaDsr.runDeadlineSweep)',
    status: metaStatus(() => texasApi.ccpaDsr.getMeta()),
    run: runPost(() => texasApi.ccpaDsr.runDeadlineSweep()),
  },
  // Event Stream & Deadlines (kafka.js, ttl-deadlines.js)
  {
    key: 'consumer-lag',
    group: 'stream',
    label: 'Event consumer lag',
    description: 'How far behind the alert consumer sits on each topic.',
    source: 'routes/kafka.js GET /api/texas/kafka/alert-consumer/lag (texasApi.eventStream.getLag)',
    status: metaStatus(() => texasApi.eventStream.getLag()),
  },
  {
    key: 'dlq',
    group: 'stream',
    label: 'DLQ backlog',
    description: 'Schema/processing dead-letter queue sizes and error counts.',
    source: 'routes/kafka.js GET /api/texas/kafka/alert-consumer/dlq (texasApi.eventStream.listDlq)',
    status: metaStatus(() => texasApi.eventStream.listDlq()),
  },
  {
    key: 'deadline-summary',
    group: 'stream',
    label: 'Deadline monitor summary',
    description: 'Active TTL deadlines by family and urgency bucket.',
    source: 'routes/ttl-deadlines.js GET /api/texas/ttl-deadlines/summary (texasApi.deadlineMonitor.getSummary)',
    status: metaStatus(() => texasApi.deadlineMonitor.getSummary()),
  },
  // Engine Status Probes (read only)
  {
    key: 'compliance-meta',
    group: 'probes',
    label: 'Compliance dashboard',
    description: 'Compliance engine availability for Dallas / Houston portfolios.',
    source: 'routes/texas-compliance-dashboard.js GET /api/texas/compliance-dashboard/meta :33',
    status: metaStatus(() => texasApi.complianceDashboard.getMeta()),
  },
  {
    key: 'security-score-meta',
    group: 'probes',
    label: 'Security & safety score',
    description: 'Security score engine availability and coverage.',
    source: 'routes/security-score.js GET /api/texas/security-score/meta :53',
    status: metaStatus(() => texasApi.securityScore.getMeta()),
  },
  {
    key: 'flood-meta',
    group: 'probes',
    label: 'Flood zone service',
    description: 'NFHL-backed flood lookups and zone-history freshness.',
    source: 'routes/flood.js GET /api/texas/flood/meta (floodApi.getMeta)',
    status: metaStatus(() => floodApi.getMeta()),
  },
  {
    key: 'city-overrides-meta',
    group: 'probes',
    label: 'City override rules',
    description: 'Override actions available and rule counts by priority chain.',
    source: 'routes/city-overrides.js GET /api/texas/city-overrides/meta :67',
    status: metaStatus(() => cityOverridesApi.getMeta()),
  },
  {
    key: 'deposits-meta',
    group: 'probes',
    label: 'Deposit tracker',
    description: '30-day deposit return tracker availability.',
    source: 'routes/texas-deposit.js GET /api/texas/deposits/meta (texasApi.deposits.getMeta)',
    status: metaStatus(() => texasApi.deposits.getMeta()),
  },
  {
    key: 'rekey-meta',
    group: 'probes',
    label: '7-day re-keying',
    description: 'Rekey ledger engine availability.',
    source: 'routes/rekeying-ledger.js GET /api/texas/rekey/meta (texasApi.rekeyLedger.getMeta)',
    status: metaStatus(() => texasApi.rekeyLedger.getMeta()),
  },
  {
    key: 'lead-scoring-meta',
    group: 'probes',
    label: 'Lead scoring',
    description: 'Lead scoring engine availability.',
    source: 'routes/lead-scoring.js GET /api/lead-scoring/meta :40',
    status: metaStatus(() => landlordApi.get('api/lead-scoring/meta')),
  },
  {
    key: 'registration-summary',
    group: 'probes',
    label: 'Dallas registration portfolio',
    description: 'Dallas rental registration scorecard across the portfolio.',
    source: 'routes/dallas-registration-score.js GET /api/texas/dallas/registration-score/portfolio/summary',
    status: metaStatus(() => texasApi.dallasRegistrationScore.getPortfolioSummary()),
  },
];

async function safe(fn: () => Promise<OpsTaskResult>): Promise<OpsTaskResult> {
  try {
    return await fn();
  } catch (err: any) {
    return { ok: false, summary: err?.message || 'failed' };
  }
}

export const TexasOpsConsole: React.FC = () => {
  const [results, setResults] = useState<Record<string, OpsTaskResult>>({});
  const [runBusy, setRunBusy] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [lastRun, setLastRun] = useState<string | null>(null);

  const probeAll = useCallback(async () => {
    setLoading(true);
    const entries = await Promise.all(
      OPS_TASKS.map(async (t) => {
        const r = await safe(t.status);
        return [t.key, r] as const;
      }),
    );
    const map: Record<string, OpsTaskResult> = {};
    entries.forEach(([k, r]) => {
      map[k] = r;
    });
    setResults(map);
    setLastRun(new Date().toISOString());
    setLoading(false);
  }, []);

  useEffect(() => {
    probeAll();
  }, [probeAll]);

  const onRun = async (task: OpsTask) => {
    if (!task.run) return;
    setRunBusy((b) => ({ ...b, [task.key]: true }));
    setResults((r) => ({ ...r, [task.key]: { ok: false, summary: 'Running…' } }));
    const res = await safe(task.run);
    setResults((r) => ({ ...r, [task.key]: res }));
    setRunBusy((b) => ({ ...b, [task.key]: false }));
  };

  if (getAppMarket() !== 'texas') {
    return null;
  }

  return (
    <div data-testid="ops-console" className="texas-page-container">
      <h1>Texas Operations Console</h1>
      <p className="page-description">
        Run and observe Texas ops tasks — metering, compliance checks, event
        stream health and engine status. All reads and actions use existing
        backend endpoints.
      </p>

      <div className="mb-6 flex items-center gap-3">
        <button
          data-testid="ops-refresh"
          onClick={probeAll}
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <i className="fas fa-sync-alt mr-2"></i> Refresh all
        </button>
        {lastRun && (
          <span className="text-xs text-gray-500" data-testid="ops-last-refreshed">
            Last refreshed {lastRun}
          </span>
        )}
      </div>

      {loading && (
        <div className="text-sm text-gray-500 mb-4">
          <i className="fas fa-spinner fa-spin mr-1"></i> Probing tasks…
        </div>
      )}

      <div className="space-y-8">
        {OPS_GROUPS.map((group) => (
          <section key={group.key} data-testid={`ops-group-${group.key}`}>
            <h2 className="text-lg font-bold text-gray-900 mb-3">{group.label}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {OPS_TASKS.filter((t) => t.group === group.key).map((task) => {
                const res = results[task.key];
                return (
                  <div
                    key={task.key}
                    data-testid={`ops-task-${task.key}`}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900">{task.label}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{task.description}</div>
                      </div>
                      <span
                        data-testid={`ops-status-${task.key}`}
                        className={`inline-flex items-center shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${
                          res?.ok ? 'bg-green-100 text-green-800' : res ? 'bg-red-100 text-red-800' : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {res?.ok ? 'OK' : res ? 'ERROR' : '…'}
                      </span>
                    </div>
                    <div className="mt-3 text-sm text-gray-700 break-words" data-testid={`ops-detail-${task.key}`}>
                      {res ? res.summary : '—'}
                    </div>
                    <div className="mt-3 text-xs text-gray-400">{task.source}</div>
                    {task.run && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <button
                          data-testid={`ops-run-${task.key}`}
                          disabled={runBusy[task.key]}
                          onClick={() => onRun(task)}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                        >
                          {runBusy[task.key] ? (
                            <>
                              <i className="fas fa-spinner fa-spin mr-2"></i> Running…
                            </>
                          ) : (
                            <>
                              <i className="fas fa-play mr-2"></i> Run
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default TexasOpsConsole;